import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    FC,
} from "react";
import { useForm, useValidator, useMeta } from "@/hooks";
import {
    ScheduleProviderProps,
    ScheduleFormContextType,
    ScheduleFormInputs,
    Schedule,
    StoreOption,
    InventoryOption,
    Coordinates,
    DayName,
    DayInputs,
    DayErrorsMap,
} from "./types";
import { getDayAndMonth } from "./utils";
import { useScheduleValidation, useScheduleData } from "@/hooks";
import { useStoreContext } from "context/store";

const ScheduleContext = createContext<ScheduleFormContextType | null>(null);

export const useScheduleContext = () => {
    const context = useContext(ScheduleContext);
    if (!context) {
        throw new Error(
            "useScheduleContext must be used inside ScheduleProvider",
        );
    }
    return context;
};

export const ScheduleProvider: FC<ScheduleProviderProps> = ({
    isCreate,
    isUpdating,
    schedules,
    selectSchedule,
    handlerUpdatingOrCreate,
    contextType,
    defaultCoordinates,
    children,
}) => {
    const {
        providers,
        treezSellConfigsLoading,
        getTreezStoreContextByStoreId,
    } = useStoreContext();

    const { user } = useMeta();
    const { inputs, setInputs, selectChange, selectValue, inputChange } =
        useForm();

    // ============================================================================
    // SPECIALIZED HOOKS
    // ============================================================================

    const {
        runDayValidation,
        validateAllDays,
        hasAnyWorkingHoursInWeek,
        DAYS: VALIDATION_DAYS,
    } = useScheduleValidation();

    const {
        transformScheduleToInputs,
        transformInputsToSchedule,
        getDaySnapshot,
        handleStoreProviderChange: handleStoreProviderChangeData,
        getInitialInputs,
    } = useScheduleData();

    // ============================================================================
    // LOCAL STATE
    // ============================================================================

    const [activeTab, setActiveTab] = useState("0");
    const [disableTimeWindows, setDisableTimeWindows] = useState(false);

    const defaultDayWithErrors = useMemo(
        () =>
            VALIDATION_DAYS.reduce<DayErrorsMap>((acc, d) => {
                acc[d] = {};
                return acc;
            }, {} as DayErrorsMap),
        [VALIDATION_DAYS],
    );

    const [dayWithErrors, setDayWithErrors] =
        useState<DayErrorsMap>(defaultDayWithErrors);

    // Inventory and coordinates state
    const [inventories, setInventories] = useState<InventoryOption[]>([]);
    const [storeCoordinates, setStoreCoordinates] =
        useState<Coordinates | null>(defaultCoordinates ?? null);

    const providerId =
        inputs?.stores_provider_id ?? providers?.[0]?.value ?? null;

    const loadingInventories = Boolean(
        providerId &&
            providers.length > 0 &&
            !user.loading &&
            treezSellConfigsLoading,
    );

    // Inventories + map center from Sell Treez store config (same source as POS Treez)
    useEffect(() => {
        if (user.loading) {
            return;
        }

        if (!providerId || providers.length === 0) {
            setInventories([]);
            return;
        }

        if (treezSellConfigsLoading) {
            return;
        }

        const ctx = getTreezStoreContextByStoreId(String(providerId));

        if (!ctx) {
            setInventories([]);
            setStoreCoordinates(prev => prev ?? defaultCoordinates ?? null);
            return;
        }

        setInventories(ctx.locations ?? []);

        const lat = Number(ctx.latitude);
        const lng = Number(ctx.longitude);
        const valid = Number.isFinite(lat) && Number.isFinite(lng);

        if (valid) {
            setStoreCoordinates({ lat, lng });
        } else {
            setStoreCoordinates(prev => prev ?? defaultCoordinates ?? null);
        }
    }, [
        user.loading,
        providerId,
        providers.length,
        treezSellConfigsLoading,
        defaultCoordinates?.lat,
        defaultCoordinates?.lng,
        getTreezStoreContextByStoreId,
    ]);

    // ============================================================================
    // FORM VALIDATORS
    // ============================================================================

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "name",
                method: "isEmpty",
                validWhen: false,
                message: "The Name is required.",
            },
            {
                field: "stores_provider_id",
                method: "isEmpty",
                validWhen: false,
                message: "The Store provider id is required.",
            },
        ],
        { ...inputs },
    );

    const {
        validator: pickupValidator,
        isValid: isValidPickup,
        isSubmited: isSubmitPickup,
    } = useValidator(
        [
            {
                field: "name",
                method: "isEmpty",
                validWhen: false,
                message: "The Name is required.",
            },
            {
                field: "stores_provider_id",
                method: "isEmpty",
                validWhen: false,
                message: "The Store provider id is required.",
            },
        ],
        { ...inputs },
    );

    // ============================================================================
    // DATA HYDRATION
    // ============================================================================

    useEffect(() => {
        if (!isCreate) {
            const selected = schedules?.[selectSchedule] as
                | Schedule
                | undefined;
            if (!selected) return;

            const transformedInputs = transformScheduleToInputs(
                selected,
                contextType,
            );
            setInputs(transformedInputs);
        }

        if (isCreate) {
            const initialInputs = getInitialInputs(contextType);
            setInputs(prev => ({ ...prev, ...initialInputs }));
        }
    }, [isCreate, schedules, selectSchedule, contextType]);

    // Separate effect for initial coordinates setup
    useEffect(() => {
        if (!storeCoordinates && defaultCoordinates) {
            setStoreCoordinates(defaultCoordinates);
        }
    }, [storeCoordinates, defaultCoordinates]);

    // ============================================================================
    // ERROR HANDLING
    // ============================================================================

    const totalErrors = useMemo(() => {
        return Object.values(dayWithErrors).reduce(
            (acc, obj) => acc + Object.keys(obj || {}).length,
            0,
        );
    }, [dayWithErrors]);

    const showInvalidMessage = totalErrors > 0;

    // ============================================================================
    // VALIDATION FUNCTIONS
    // ============================================================================

    const validateDayAndStore = useCallback(
        (day: DayName, overrides?: Partial<DayInputs>): boolean => {
            const snapshot = getDaySnapshot(
                day,
                inputs as ScheduleFormInputs,
                overrides,
            );
            const { mergedErrors, hasInvalid } = runDayValidation(
                snapshot,
                disableTimeWindows,
            );

            setDayWithErrors(prev => {
                const prevJson = JSON.stringify(prev[day] || {});
                const nextJson = JSON.stringify(mergedErrors);
                if (prevJson === nextJson) return prev;
                return { ...prev, [day]: mergedErrors };
            });

            return !hasInvalid;
        },
        [inputs, disableTimeWindows, getDaySnapshot, runDayValidation],
    );

    const validateAllTabsAndShow = (): boolean => {
        const {
            nextErrors: initialErrors,
            hasErrors: initialHasErrors,
            firstBadIndex: initialFirstBadIndex,
        } = validateAllDays(
            (day: DayName) => getDaySnapshot(day, inputs as ScheduleFormInputs),
            disableTimeWindows,
        );

        const nextErrors = { ...initialErrors };
        let hasErrors = initialHasErrors;
        let firstBadIndex = initialFirstBadIndex;

        // Check if there's at least one day with working hours
        if (!hasAnyWorkingHoursInWeek(inputs?.working_hours ?? [])) {
            nextErrors.MONDAY = {
                ...(nextErrors.MONDAY || {}),
                general:
                    "At least one day with working hours (start & end) is required.",
            };
            if (firstBadIndex === null) firstBadIndex = 0;
            hasErrors += 1;
        }

        setDayWithErrors(nextErrors);

        if (hasErrors > 0) {
            if (firstBadIndex !== null) setActiveTab(String(firstBadIndex));
            return false;
        }
        return true;
    };

    // ============================================================================
    // ACTION HANDLERS
    // ============================================================================

    const handleStoreProviderChange = (option: StoreOption) => {
        handleStoreProviderChangeData(
            option,
            inputs as ScheduleFormInputs,
            setInputs,
            contextType,
        );
    };

    const clearTimeSlotsForAllDay = () => {
        setInputs(prev => ({ ...prev, time_slots: [] }));
        setDisableTimeWindows(prev => !prev);
    };

    const handleShowTabErrors = () => {
        const idx = VALIDATION_DAYS.findIndex(day => {
            const d = dayWithErrors[day];
            return d && Object.keys(d).length > 0;
        });
        if (idx >= 0) setActiveTab(String(idx));
    };

    const createOrUpdate = async () => {
        // Mark form submitted first so name / stores_provider_id errors render
        // even when working-hours validation also fails.
        isSubmited?.(true);
        isSubmitPickup?.(true);

        const isDelivery = contextType === "Delivery";
        const formOk = isDelivery ? isValid() : isValidPickup();
        const allOk = validateAllTabsAndShow();

        if (!formOk || !allOk) return;

        const newSchedule = transformInputsToSchedule(
            inputs as ScheduleFormInputs,
            contextType,
        );

        const next: Schedule[] = [...(schedules ?? [])];
        if (isCreate) {
            next.push(newSchedule);
        } else {
            next[selectSchedule] = newSchedule;
        }

        handlerUpdatingOrCreate(next);
    };

    // ============================================================================
    // MESSAGE HELPERS
    // ============================================================================

    const getFirstMessageGeneral = (d?: DayName): string | null => {
        if (!d) return null;
        const g = dayWithErrors?.[d]?.general;
        return typeof g === "string" && g ? g : null;
    };

    const getActiveMessage = useMemo<string | null>(() => {
        const idx = Number.parseInt(activeTab, 10);
        const activeDay = VALIDATION_DAYS[idx] as DayName | undefined;

        let activeError =
            "Invalid time slot / working hour configurations found. Please correct them before saving.";

        // General message of active day
        const activeGeneral = getFirstMessageGeneral(activeDay);
        if (activeGeneral) {
            activeError = activeGeneral;
        }

        // Search for the first general from the next day (with wrap-around)
        const rotated = [
            ...VALIDATION_DAYS.slice(idx + 1),
            ...VALIDATION_DAYS.slice(0, idx),
        ];
        const nextDayWithGeneral = rotated.find(
            d => !!getFirstMessageGeneral(d as DayName),
        );
        const nextGeneral = getFirstMessageGeneral(
            nextDayWithGeneral as DayName | undefined,
        );
        if (nextGeneral) {
            activeError = nextGeneral;
        }

        if (totalErrors) {
            return `${totalErrors} error(s) - ${activeError}`;
        }

        return null;
    }, [activeTab, dayWithErrors, totalErrors, VALIDATION_DAYS]);

    // ============================================================================
    // CONTEXT VALUE
    // ============================================================================

    const value: ScheduleFormContextType = {
        context: contextType,
        inputs: inputs as ScheduleFormInputs,
        setInputs,
        activeTab,
        setActiveTab,
        disableTimeWindows,
        setDisableTimeWindows,
        dayWithErrors,
        setDayWithErrors,
        showInvalidMessage,
        inventories,
        setInventories,
        loadingInventories,
        storeCoordinates,
        setStoreCoordinates,
        getActiveMessage,
        isUpdating,
        isCreate,

        validator,
        isValid,
        isSubmited,
        pickupValidator,
        isValidPickup,
        isSubmitPickup,

        getDayAndMonth: getDayAndMonth as unknown as (d: DayName) => {
            day: number;
            month: number;
        },

        handleStoreProviderChange,
        clearTimeSlotsForAllDay,
        handleShowTabErrors,
        createOrUpdate,
        inputChange,
        selectChange,
        selectValue,

        validateDayAndStore,
        validateAllTabsAndShow,
    };

    return (
        <ScheduleContext.Provider value={value}>
            {children}
        </ScheduleContext.Provider>
    );
};
